import { useShallow } from 'zustand/react/shallow';

import { getTopNotEarners } from '@/shared/api/wheel/get-top-not-earners';
import { getWheelPrizes as getWheelSectors } from '@/shared/api/wheel/get-wheel-prizes';
import { spin, spinX } from '@/shared/api/wheel/spin';
import { setCloudStorageValue } from '@/shared/helpers/cloud-storage';
import { createClearableStore } from '@/shared/helpers/create-clearable-store';

import { MAX_DAILY_ADS_TICKETS, WHEEL_ADS_COUNTER_KEY } from '../constants';
import { WheelMode, type WheelStore } from './wheel.types';

const defaultWheelState = {
  spinning: false,
  prizes: [],
  sectors: undefined,
  showReward: false,
  isCompletedPoll: false,
  doublePrizeTaskUrl: null,
  isActiveAd: false,

  adsCounter: 0,
  canWatchAd: false,

  topEarners: undefined,
};

const useWheelStateRaw = createClearableStore<WheelStore>()((set, get) => ({
  ...defaultWheelState,
  actions: {
    hydrate: (fresh: Partial<WheelStore>) => set(fresh),
    spin: async (mode: WheelMode) => {
      set({ spinning: true });

      try {
        const response = await (mode === WheelMode.SPIN ? spin : spinX)();

        if (!response) throw new Error('No response from spin API');

        const { prizes, doublePrizeTaskUrl } = response;
        set({ prizes, doublePrizeTaskUrl });

        return prizes;
      } catch (error) {
        console.error('Spin error:', error);
        set({ spinning: false });
        return undefined;
      }
    },
    fetchWheelInitialData: async () => {
      try {
        const sectors = await getWheelSectors();

        set({
          sectors,
        });

        return sectors;
      } catch (err) {
        console.error('fetchWheelInitialData ERROR', err);
      }
    },

    setIsActiveAd: (isActiveAd) => {
      set({ isActiveAd });
    },
    incrementAdsCounter: () => {
      const newCounter = get().adsCounter + 1;
      set({ adsCounter: newCounter });
      void setCloudStorageValue(WHEEL_ADS_COUNTER_KEY, String(newCounter));
      set({ canWatchAd: newCounter < MAX_DAILY_ADS_TICKETS });
    },
    setAdsCounter: (newAdsCounter) => {
      set({
        adsCounter: newAdsCounter,
        canWatchAd: newAdsCounter < MAX_DAILY_ADS_TICKETS,
      });
    },

    fetchTopEarners: async () => {
      try {
        const topNotEarners = await getTopNotEarners();

        set({ topEarners: topNotEarners });

        return topNotEarners;
      } catch (err) {
        console.error('fetchTopEarners ERROR', err);

        set({ topEarners: [] });

        return [];
      }
    },
  },
}));

useWheelStateRaw.subscribe((state, prevState) => {
  const gotNewPrize = state.prizes !== prevState.prizes;
  const spinningChanges = state.spinning !== prevState.spinning;

  const showReward =
    (gotNewPrize || spinningChanges) && !!(state.prizes?.length && !state.spinning);

  if (showReward) {
    state.actions.hydrate({ showReward });
  }
});

export const useWheelState = () =>
  useWheelStateRaw(
    useShallow((state) => ({
      spinning: state.spinning,
      prizes: state.prizes,
      sectors: state.sectors,
      showReward: state.showReward,
      isCompletedPoll: state.isCompletedPoll,
      doublePrizeTaskUrl: state.doublePrizeTaskUrl,
      isActiveAd: state.isActiveAd,

      adsCounter: state.adsCounter,
      canWatchAd: state.canWatchAd,
    }))
  );

export const useWheelSpining = () =>
  useWheelStateRaw(useShallow((state) => state.spinning));

export const useWheelTopEarners = () =>
  useWheelStateRaw(useShallow((state) => state.topEarners));

export const useWheelActions = () => useWheelStateRaw((state) => state.actions);
